import { useCallback } from 'react';

import {
  getOrderPdf,
  getPatientCommunicationPdf,
  getVisitPdf,
} from '../services/api';
import type {
  BatchPrintTaskRef,
  TaskListResponse,
} from '../services/api/types.gen';
import { PRINTABLE_TASK_TYPES } from '../components/task/batchActions';
import { usePdfExport, type PdfSource } from './usePdfExport';

export interface UsePrintViewResult {
  /** Open the given task's PDF in a new browser tab. */
  printTask: (task: TaskListResponse) => Promise<void>;
  /** Id of the task currently being printed, or null when idle. */
  printingTaskId: string | null;
}

/**
 * True when the task type has a rendered PDF. Misc tasks do not, so callers hide
 * the print affordance rather than offering one that resolves to nothing. Backed
 * by the same allowlist the batch-print filter uses.
 */
export function hasPrintView(
  task: Pick<TaskListResponse, 'task_type'>
): boolean {
  return PRINTABLE_TASK_TYPES.has(
    task.task_type as BatchPrintTaskRef['task_type']
  );
}

/**
 * Per-row "Print view" for episode-manager tasks. Dispatches on `task_type` to
 * the matching PDF endpoint (keyed by the shared polymorphic `task.id`) and
 * opens the result in a new tab via {@link usePdfExport}.
 *
 * Note: orders use the generic order PDF only. CMS-485 signed URLs are
 * reachable by opening the task's detail panel.
 */
export function usePrintView(): UsePrintViewResult {
  const { exportPdf, exportingId } = usePdfExport();

  const printTask = useCallback(
    (task: TaskListResponse): Promise<void> =>
      exportPdf(
        task.id,
        async (): Promise<PdfSource | null> => {
          const request = (() => {
            switch (task.task_type) {
              case 'visit':
                return getVisitPdf({ path: { visit_id: task.id } });
              case 'order':
                return getOrderPdf({ path: { order_id: task.id } });
              case 'patient_communication':
                return getPatientCommunicationPdf({
                  path: { communication_id: task.id },
                });
              default:
                return null;
            }
          })();
          if (!request) return null;
          const response = await request;
          return response.data
            ? {
                blob: new Blob([response.data as BlobPart], {
                  type: 'application/pdf',
                }),
              }
            : null;
        },
        'task PDF'
      ),
    [exportPdf]
  );

  return { printTask, printingTaskId: exportingId };
}
